import React from 'react'
import { useSelector } from 'react-redux'
import { matchPath, Route, Switch } from 'react-router'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import type { AppRoute } from 'src/navigation/appRoutes'
import APIKeysAppRoute from 'src/navigation/appRoutes/organization/APIKeysAppRoute'
import AutomationSettingsAppRoute from 'src/navigation/appRoutes/organization/automationSettingsAppRoute'
import BankAccountsAppRoute from 'src/navigation/appRoutes/organization/bankAccounts.appRoute'
import BusinessRulesAppRoute from 'src/navigation/appRoutes/organization/businessRules.appRoute'
import CardFeedCreateAppRoute from 'src/navigation/appRoutes/organization/cardFeedCreate.appRoute'
import CardFeedDetailsAppRoute from 'src/navigation/appRoutes/organization/cardFeedDetails.appRoute'
import CardFeedsAppRoute from 'src/navigation/appRoutes/organization/cardFeeds.appRoute'
import CustomFieldDetailAppRoute from 'src/navigation/appRoutes/organization/customFieldDetail.appRoute'
import DepartmentDetailsAppRoute from 'src/navigation/appRoutes/organization/departmentDetails.appRoute'
import DepartmentsAppRoute from 'src/navigation/appRoutes/organization/departments.appRoute'
import ExpenseCategoriesAppRoute from 'src/navigation/appRoutes/organization/expenseCategories.appRoute'
import ExpenseCategoryLabelsAppRoute from 'src/navigation/appRoutes/organization/expenseCategoryLabels.appRoute'
import ExpenseRequirementsAppRoute from 'src/navigation/appRoutes/organization/expenseRequirements.appRoute'
import ManageSubscriptionAppRoute from 'src/navigation/appRoutes/organization/manageSubscription.appRoute'
import OrganizationInfoAppRoute from 'src/navigation/appRoutes/organization/organizationInfo.appRoute'
import OrganizationIntegrationsAppRoute from 'src/navigation/appRoutes/organization/organizationIntegrations.appRoute'
import OrgSettingsReimbursementsAppRoute from 'src/navigation/appRoutes/organization/reimbursements.appRoute'
import SSOSettingsAppRoute from 'src/navigation/appRoutes/organization/ssoSettings.appRoute'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

/**
 * The organization-settings routes, in the SAME precedence order as the legacy Authed
 * background <Switch> (src/app/Authed.tsx — `authedBackgroundAppRoute.subRoutes` first,
 * then the `updateAuthedBackgroundAppRoute` appends). Lifting the exact order + the
 * `exceptions` filter (below) makes this page behavior-identical to the legacy registry it
 * replaces. Each entry keeps its own `routeProps` (some `exact: false`) so the parent pages
 * that own internal sub-tabs — ExpenseCategories (account/custom), ExpenseRequirements
 * (receiptrules/additionalfields), AutomationSettings (rules/expensesettings) — keep their
 * nested <Switch> unchanged ("keep local tabs" exception, migration-checklist).
 *
 * Out of lane (NOT here — stay legacy overlays, EX-39749): manageOrgIntegration (popup),
 * cancelSubscription (popup).
 */
const ORGANIZATION_SETTINGS_ROUTES: AppRoute[] = [
  // from authedBackgroundAppRoute.subRoutes (spread first in the legacy switch)
  ExpenseCategoryLabelsAppRoute, // /expensecategories/:expenseCategoryId (skips account/custom via exceptions)
  DepartmentDetailsAppRoute, // /departments/:departmentId
  // from updateAuthedBackgroundAppRoute appends (order matters: specific paths first)
  CustomFieldDetailAppRoute, // /expensecategories/custom/:customFieldId
  ExpenseCategoriesAppRoute, // /expensecategories (exact:false → owns account/custom)
  DepartmentsAppRoute, // /departments
  CardFeedsAppRoute, // /cardfeeds
  CardFeedCreateAppRoute, // /cardfeeds/create
  CardFeedDetailsAppRoute, // /cardfeeds/:id
  OrganizationIntegrationsAppRoute, // /integrations
  BusinessRulesAppRoute, // /businessrules
  ExpenseRequirementsAppRoute, // /expenserequirements (exact:false → owns receiptrules/additionalfields)
  OrgSettingsReimbursementsAppRoute, // /reimbursements
  OrganizationInfoAppRoute, // /organizationinfo
  BankAccountsAppRoute, // /bankaccounts (Bill Pay M1 — org pay-from bank accounts)
  ManageSubscriptionAppRoute, // /managesubscription
  SSOSettingsAppRoute, // /sso
  APIKeysAppRoute, // /apikeys
  AutomationSettingsAppRoute, // /automation (exact:false)
]

/**
 * OrganizationSettingsPage — route-level page for the organization-settings family
 * (EX-39748). Registered ONCE in the flat route registry for the whole family (see
 * flatArchitectureRoutes.tsx + organizationRouteTree.ts), then owns the nested tab routing
 * via the <Switch> below.
 *
 * It mirrors the ACCOUNTS family page's deliberate "no horizontal tab bar" choice: the
 * organization-settings family is navigated through the persistent left settings Sidenav
 * (`SidenavContentType.Settings`, mounted in the Authed shell), not a FamilyLayout Subnav.
 * So this page renders no tab bar of its own — it is purely the routing boundary that swaps
 * the matched settings page beneath the (already-mounted) shell chrome, exactly as the
 * legacy background <Switch> did.
 *
 * The nested <Switch location={calculatedBackgroundLocation}> + `exceptions` filter are
 * lifted verbatim from the legacy Authed background switch so routing is behavior-identical.
 */
const OrganizationSettingsPage: React.FC = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  return (
    <Switch location={calculatedBackgroundLocation}>
      {ORGANIZATION_SETTINGS_ROUTES.map(
        (route, i) =>
          !route.exceptions?.some((exc) =>
            matchPath(calculatedBackgroundLocation.pathname, exc.routeProps),
          ) && (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ),
      )}
      <Route path={'/'}>
        <NotFoundBackgroundPageContent />
      </Route>
    </Switch>
  )
}

export default OrganizationSettingsPage
